import { useState } from 'react';
import {
  NAME_LISTS,
  namesDefaults,
  parseNameColumn,
  type NameListKey,
  type NamesDef,
} from '../../../lib/names/types';
import { Fieldset, StringListEditor, inputCls, smallBtn } from '../fields';
import type { FormProps } from '../types';

const TITLES: Record<NameListKey, string> = {
  male: 'Male first names',
  female: 'Female first names',
  last: 'Last names',
};

/**
 * The `names` section (CONTRACT §13.5): the pools `Names/NameFactory` draws real names from for every survivor or NPC made without a
 * curated one (the Stranger, raid recruits, stragglers, occupied crews, dev survivors; story cast and QR copies keep theirs). Three
 * lists, each edited row by row (add, remove, reorder) or by pasting a column; the validator (`validate/names.ts`, a port of
 * `ContentValidator.Names.cs`) shows under the list and the row.
 */
export function NamesForm({ value, onChange, errors }: FormProps<NamesDef>) {
  const v = { ...namesDefaults(), ...value };
  return (
    <>
      <p className="mb-2 text-xs text-bruise">
        1960s-American names, Classic POP era. Each entry is one capitalised word: letters, with
        apostrophes or hyphens inside (O&apos;Neil, Mary-Lou). No repeats within a list. The sex
        comes from the survivor&apos;s appearance code, so the first name always matches the body.
      </p>
      <div className="grid gap-3 lg:grid-cols-3">
        {NAME_LISTS.map((key) => (
          <NameList
            key={key}
            list={key}
            value={(v[key] ?? []).map((n) => n ?? '')}
            errors={errors}
            onChange={(next) => onChange({ ...v, [key]: next })}
          />
        ))}
      </div>
    </>
  );
}

function NameList({
  list,
  value,
  errors,
  onChange,
}: {
  list: NameListKey;
  value: string[];
  errors: FormProps<NamesDef>['errors'];
  onChange: (next: string[]) => void;
}) {
  const [paste, setPaste] = useState('');
  const pasted = parseNameColumn(paste);
  const title = TITLES[list];
  return (
    <Fieldset legend={`${title} (${value.length})`}>
      <div className="sm:col-span-2">
        <label className="text-xs font-semibold">
          Paste a column
          <textarea
            aria-label={`${title} paste`}
            className={`${inputCls} block h-20 font-mono`}
            placeholder={'Walt\nEarl\nDolores'}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
          />
        </label>
        <div className="mt-1 flex gap-2">
          <button
            type="button"
            className={smallBtn}
            disabled={pasted.length === 0}
            onClick={() => {
              onChange([...value, ...pasted]);
              setPaste('');
            }}
          >
            Append {pasted.length}
          </button>
          <button
            type="button"
            className={smallBtn}
            disabled={pasted.length === 0}
            onClick={() => {
              onChange(pasted);
              setPaste('');
            }}
          >
            Replace with {pasted.length}
          </button>
        </div>
        <StringListEditor
          label={title}
          value={value}
          onChange={onChange}
          errors={errors}
          base={list}
          placeholder="Name"
        />
      </div>
    </Fieldset>
  );
}
